import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { sendWhatsAppMessage } from '@/lib/whatsappSender';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Zap, Send, Loader2, Search, ArrowLeft, MessageSquareText, FileText, Image as ImageIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Template {
  id: string;
  template_name: string;
  nome_exibicao: string;
  corpo: string;
  variaveis: any[] | null;
  categoria: string | null;
  status_meta: string | null;
  language: string | null;
}

interface TemplateSelectorPopoverProps {
  conversation: {
    id: string;
    telefone: string;
    status: string;
    connection_id?: string | null;
  };
  profileId?: string;
  onSent?: () => void;
  onInsertText?: (text: string) => void;
  triggerAsButton?: boolean;
}

export function TemplateSelectorPopover({ conversation, profileId, onSent, onInsertText, triggerAsButton }: TemplateSelectorPopoverProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Template | null>(null);
  const [varValues, setVarValues] = useState<Record<number, string>>({});
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'templates' | 'rapidas'>('rapidas');

  // 360dialog ABOLIDO: templates HSM (sistema morto) NÃO são mais carregados.
  // Só Evolution -> usar respostas rápidas. Mantido vazio pra não quebrar o resto do componente.
  const templates: Template[] = [];

  const { data: respostasRapidas = [] } = useQuery({
    queryKey: ['whatsapp-respostas-rapidas-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whatsapp_respostas_rapidas')
        .select('id, titulo, categoria, corpo, imagem_url')
        .eq('ativo', true)
        .order('categoria')
        .order('titulo');
      if (error) throw error;
      return data || [];
    },
    staleTime: 30_000,
  });

  const filtered = templates.filter(t =>
    t.nome_exibicao.toLowerCase().includes(search.toLowerCase()) ||
    t.template_name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredRapidas = respostasRapidas.filter((r: any) =>
    r.titulo.toLowerCase().includes(search.toLowerCase()) ||
    r.corpo.toLowerCase().includes(search.toLowerCase())
  );

  const getPreview = () => {
    if (!selected) return '';
    let preview = selected.corpo || '';
    const vars = (selected.variaveis || []) as { pos: number; nome?: string }[];
    for (const v of vars) {
      const val = varValues[v.pos] || `{{${v.pos}}}`;
      preview = preview.replace(`{{${v.pos}}}`, val);
    }
    return preview;
  };

  // Send image from resposta rápida as media message
  const handleSendImageRapida = async (rapida: any) => {
    if (sending) return;
    setSending(true);

    try {
      const mediaUrl = rapida.imagem_url;
      const caption = rapida.corpo || null;

      // 1. Save outbound message
      const { data: msgData, error: msgErr } = await supabase
        .from('whatsapp_messages')
        .insert({
          conversation_id: conversation.id,
          direction: 'outbound',
          type: 'image',
          body: caption,
          media_url: mediaUrl,
          mime_type: 'image/jpeg',
          status: 'pending',
          message_source: 'humano',
          sent_by_user_id: profileId,
        })
        .select()
        .single();
      if (msgErr) throw msgErr;

      // 2. Send (provider 360dialog ou Evolution, decidido pelo helper)
      const { data: sendResult, error: sendErr } = await sendWhatsAppMessage({
        to: conversation.telefone,
        media_url: mediaUrl,
        media_type: 'image',
        mime_type: 'image/jpeg',
        caption: caption,
        conversation_id: conversation.id,
        message_id: msgData?.id,
      });
      if (sendErr) throw sendErr;

      if (sendResult && sendResult.success === false) {
        throw new Error(sendResult.error || 'Erro ao enviar imagem');
      }

      // 3. If there's body text, send it as a separate text message after the image
      if (caption) {
        const { data: txtMsg, error: txtErr } = await supabase
          .from('whatsapp_messages')
          .insert({
            conversation_id: conversation.id,
            direction: 'outbound',
            type: 'text',
            body: caption,
            status: 'pending',
            message_source: 'humano',
            sent_by_user_id: profileId,
          })
          .select()
          .single();
        if (txtErr) throw txtErr;

        const { data: txtSend, error: txtSendErr } = await supabase.functions.invoke(
          'whatsapp-360dialog-send',
          {
            body: {
              to: conversation.telefone,
              text: caption,
              conversation_id: conversation.id,
              message_id: txtMsg?.id,
            },
          }
        );
        if (txtSendErr) throw txtSendErr;
        if (txtSend && txtSend.success === false) {
          throw new Error(txtSend.error || 'Erro ao enviar texto');
        }
      }

      // 4. Update conversation
      await supabase
        .from('whatsapp_conversations')
        .update({
          last_message_preview: caption ? caption.substring(0, 50) : `📷 ${rapida.titulo}`,
          last_message_at: new Date().toISOString(),
          last_outbound_at: new Date().toISOString(),
          status:
            conversation.status === 'nova' || conversation.status === 'aguardando_cliente'
              ? 'em_atendimento'
              : conversation.status,
        })
        .eq('id', conversation.id);

      toast.success('Card enviado!');
      setOpen(false);
      setSearch('');
      onSent?.();
    } catch (err: any) {
      toast.error('Erro ao enviar card: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!selected || sending) return;
    setSending(true);

    try {
      const vars = (selected.variaveis || []) as { pos: number }[];
      const variaveis = vars.map(v => ({
        pos: v.pos,
        valor: varValues[v.pos] || '',
      }));

      const previewText = `📋 ${selected.nome_exibicao}`;
      const { data: msgData, error: msgErr } = await supabase
        .from('whatsapp_messages')
        .insert({
          conversation_id: conversation.id,
          direction: 'outbound',
          type: 'template',
          body: getPreview() || previewText,
          status: 'pending',
          message_source: 'humano',
          sent_by_user_id: profileId,
        })
        .select()
        .single();

      if (msgErr) throw msgErr;

      const { data: sendResult, error: sendErr } = await supabase.functions.invoke(
        'whatsapp-360dialog-send',
        {
          body: {
            to: conversation.telefone,
            template_name: selected.template_name,
            variaveis,
            conversation_id: conversation.id,
            message_id: msgData?.id,
          },
        }
      );

      if (sendErr) throw sendErr;

      const result = sendResult as any;
      if (result && !result.success) {
        throw new Error(result.error || 'Falha no envio');
      }

      await supabase
        .from('whatsapp_conversations')
        .update({
          last_message_preview: previewText.slice(0, 120),
          last_message_at: new Date().toISOString(),
          last_outbound_at: new Date().toISOString(),
          status:
            conversation.status === 'nova' || conversation.status === 'aguardando_cliente'
              ? 'em_atendimento'
              : conversation.status,
        })
        .eq('id', conversation.id);

      toast.success('Template enviado!');
      setOpen(false);
      setSelected(null);
      setVarValues({});
      onSent?.();
    } catch (err: any) {
      toast.error('Erro ao enviar template: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const vars = (selected?.variaveis || []) as { pos: number; nome?: string }[];

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSelected(null); setVarValues({}); setSearch(''); setTab('rapidas'); } }}>
      <PopoverTrigger asChild>
        {triggerAsButton ? (
          <Button variant="outline" className="gap-2 border-success/40 text-success hover:bg-success/5 dark:text-success dark:border-success/30 dark:hover:bg-success/30 w-full">
            <Zap className="h-4 w-4" />
            Enviar Template
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Templates e Respostas Rápidas">
            <Zap className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="top">
        {!selected ? (
          <div className="flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setTab('rapidas')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${tab === 'rapidas' ? 'text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <MessageSquareText className="h-3.5 w-3.5" /> Rápidas
              </button>
            </div>

            {/* Search */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder={tab === 'templates' ? 'Buscar template...' : 'Buscar resposta rápida...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>

            <ScrollArea className="h-[400px]">
              <TooltipProvider delayDuration={300}>
              {tab === 'templates' ? (
                filtered.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    {templates.length === 0 ? 'Nenhum template ativo. Sincronize nas configurações.' : 'Nenhum resultado.'}
                  </p>
                ) : (
                  filtered.map((t) => (
                    <Tooltip key={t.id}>
                      <TooltipTrigger asChild>
                        <button
                          className="w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b last:border-b-0"
                          onClick={() => setSelected(t)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate flex-1">{t.nome_exibicao}</span>
                            {t.categoria && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 flex-shrink-0">
                                {t.categoria}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.corpo}</p>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="start" className="max-w-md p-3 whitespace-pre-wrap text-xs">
                        <div className="font-semibold mb-1">{t.nome_exibicao}</div>
                        <div className="text-muted-foreground">{t.corpo}</div>
                      </TooltipContent>
                    </Tooltip>
                  ))
                )
              ) : (
                filteredRapidas.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    {respostasRapidas.length === 0 ? 'Nenhuma resposta rápida. Cadastre nas configurações.' : 'Nenhum resultado.'}
                  </p>
                ) : (
                  filteredRapidas.map((r: any) => (
                    <Tooltip key={r.id}>
                      <TooltipTrigger asChild>
                        <button
                          className="w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b last:border-b-0"
                          disabled={sending}
                          onClick={() => {
                            if (r.imagem_url) {
                              // Has image — send as media
                              handleSendImageRapida(r);
                            } else {
                              // Text only — insert into input
                              onInsertText?.(r.corpo);
                              setOpen(false);
                              setSearch('');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {r.imagem_url && (
                              <img src={r.imagem_url} alt="" className="h-8 w-8 rounded object-cover flex-shrink-0 border" />
                            )}
                            <span className="text-sm font-medium truncate flex-1">{r.titulo}</span>
                            {r.imagem_url && <ImageIcon className="h-3 w-3 text-primary flex-shrink-0" />}
                            {r.categoria && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 flex-shrink-0">
                                {r.categoria}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words whitespace-pre-wrap">
                            {r.corpo || (r.imagem_url ? '📷 Card de oferta' : '')}
                          </p>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="start" className="max-w-md p-3 text-xs">
                        <div className="font-semibold mb-1 flex items-center gap-1.5">
                          {r.imagem_url && <ImageIcon className="h-3 w-3 text-primary" />}
                          {r.titulo}
                        </div>
                        {r.imagem_url && (
                          <img src={r.imagem_url} alt="" className="max-h-48 w-auto mx-auto rounded border my-2" />
                        )}
                        <div className="text-muted-foreground whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                          {r.corpo || (r.imagem_url ? '📷 Card de oferta — sem texto.' : '(sem corpo)')}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))
                )
              )}
              </TooltipProvider>
            </ScrollArea>

            {/* Sending indicator */}
            {sending && (
              <div className="flex items-center justify-center gap-2 p-2 border-t bg-muted/30">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="text-xs text-muted-foreground">Enviando card...</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="p-3 border-b flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelected(null)}>
                <ArrowLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-sm font-medium truncate">{selected.nome_exibicao}</span>
            </div>

            <div className="p-3 space-y-3">
              {vars.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Variáveis:</p>
                  {vars.map((v) => (
                    <div key={v.pos} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-16 flex-shrink-0">{`{{${v.pos}}}`}</span>
                      <Input
                        className="h-7 text-xs"
                        placeholder={v.nome || `Variável ${v.pos}`}
                        value={varValues[v.pos] || ''}
                        onChange={(e) => setVarValues(prev => ({ ...prev, [v.pos]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-muted/50 rounded-md p-2.5">
                <p className="text-xs font-medium text-muted-foreground mb-1">Preview:</p>
                <p className="text-xs whitespace-pre-wrap">{getPreview()}</p>
              </div>

              <Button
                className="w-full h-8 text-xs gap-1.5"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Enviar Template
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
